<template>
  <div class="shrink-0 glass-frosted rounded-8xl w-83 h-67.5 p-4">
    <div class="w-full h-full flex flex-col">
      <div class="flex justify-between items-center">
        <p class="text-l-medium text-black-white">{{ t('itinerary.arcGaugeSlider.title') }}</p>
        <Button color="blue2" size="xs" @click="emits('update:mode', 'calibration')">
          <Icon icon="Technology-Wireframe-Calibrate" />
          <span>{{ t('itinerary.arcGaugeSlider.calibrate') }}</span>
        </Button>
      </div>

      <div ref="svgContainer" class="relative flex-1 select-none">
        <svg
          id="gauge"
          :width="svgSize.width"
          :height="svgSize.height"
          :viewBox="`0 0 ${svgSize.width} ${svgSize.height}`"
        >
          <!-- 圆弧背景 -->
          <path
            :d="arcPath(R, START_DEG, END_DEG)"
            :stroke-width="STROKE"
            stroke="#fff"
            fill="none"
            stroke-linecap="round"
          />
          <!-- 圆弧已扫过区域 -->
          <path
            v-show="sweepDeg > 0"
            :d="arcPath(R, START_DEG, sweepDeg)"
            :stroke-width="STROKE"
            fill="none"
            stroke-linecap="round"
            :class="sweepStrokeClass"
          />

          <!-- 指针 -190 ~ 10 -->
          <g :transform="`translate(${CX}, ${CY - 10}) rotate(${pointerAngle})`">
            <path
              :d="`M 0 -1.5 L ${R + 20} -0.5 L ${R + 20} 0.5 L 0 1.5 Z`"
              class="transition-transform duration-150 ease-out fill-black-white"
            />
          </g>
          <!-- 指针圆点 -->
          <circle :cx="CX" :cy="CY - 10" r="3.5" class="fill-black-white" />
        </svg>

        <div class="absolute bottom-4 left-10 text-s-medium text-text-glass-default">0</div>
        <div class="absolute bottom-4 right-8.5 text-s-medium text-text-glass-default">255</div>

        <div
          class="absolute bottom-4 text-s-medium text-black-white left-0 right-0 flex flex-col items-center justify-center -space-y-1"
        >
          <div>~{{ realTimeTravel }}</div>
          <div>(≈{{ travelMm }}mm)</div>
        </div>
      </div>

      <div class="flex justify-center">
        <p class="text-s-medium text-text-glass-default">
          {{ t('itinerary.arcGaugeSlider.referenceOnly') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { Button, Icon } from '@rapoo/element'
  import { useI18n } from 'vue-i18n'

  const CX = 150
  const CY = 140 // circle center (bottom-center)
  const R = 105 // arc radius
  const STROKE = 25
  const START_DEG = 0
  const END_DEG = 180

  // 角度转弧度
  const polar = (angleDeg: number, r: number) => {
    const rad = ((angleDeg - 180) * Math.PI) / 180
    return {
      x: CX + r * Math.cos(rad),
      y: CY + r * Math.sin(rad),
    }
  }

  // svg 起始弧度路径
  const arcPath = (r: number, startDeg: number, endDeg: number) => {
    const s = polar(startDeg, r)
    const e = polar(endDeg, r)
    const largeArc = endDeg - startDeg > 180 ? 1 : 0
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${e.x} ${e.y}`
  }

  const props = defineProps<{
    realTimeTravel: number
    hasCrossedAbsoluteTrigger: boolean
  }>()

  const emits = defineEmits<{
    (e: 'update:mode', mode: 'calibration' | 'demo'): void
  }>()
  const { t } = useI18n()

  const svgContainer = ref<HTMLElement | null>(null)
  const svgSize = ref({ width: 0, height: 0 })

  onMounted(() => {
    if (svgContainer.value) {
      svgSize.value = {
        width: svgContainer.value.offsetWidth,
        height: svgContainer.value.offsetHeight,
      }
    }
  })

  // value(0 ~ 255) -> 指针旋转角度(-190 ~ 10)
  const pointerAngle = computed(() => -190 + (props.realTimeTravel / 255) * 200)

  // value(0 ~ 255) -> 圆弧扫过角度(0 ~ 180)
  const sweepDeg = computed(() => (props.realTimeTravel / 255) * (END_DEG - START_DEG))

  const sweepStrokeClass = computed(() =>
    props.hasCrossedAbsoluteTrigger ? 'stroke-cyan-active' : 'stroke-cyan-hover',
  )

  // value(0 ~ 255) -> mm(0 ~ 3.30)
  const travelMm = computed(() => ((props.realTimeTravel / 255) * 3.3).toFixed(2))
</script>

<style scoped></style>
